import React, { useState, useEffect } from 'react';
import { Check } from 'Components/DesignSystem/Iconography/Icons';
import { CopyWithArrowIcon } from 'assests/GmsIcons';
import MenuButton from './MenuButton';

interface TopItemsSelectorProps {
    value: TopItemsOption;
    customValue: number;
    onChange: (option: TopItemsOption, customValue?: number) => void;
    disabled?: boolean;
    isOpen: boolean;
    onToggle: () => void;
}

const noSpinnerStyles = `
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  input[type="number"] {
    -moz-appearance: textfield;
  }
`;

const TopItemsSelector = ({ value, customValue, onChange, isOpen, onToggle, disabled = false }: TopItemsSelectorProps) => {
    const [tempCustomValue, setTempCustomValue] = useState(customValue.toString());

    useEffect(() => {
        if (isOpen) {
            setTempCustomValue(customValue.toString());
        }
    }, [isOpen, customValue]);

    useEffect(() => {
        if (!isOpen && value === 'custom' && tempCustomValue !== customValue.toString()) {
            const numValue = parseInt(tempCustomValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
                onChange('custom', numValue);
            }
        }
    }, [isOpen, value, tempCustomValue, customValue, onChange]);

    const handleOptionSelect = (option: TopItemsOption) => {
        onChange(option);
        if (option !== 'custom') onToggle();
    };

    const handleCustomValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTempCustomValue(e.target.value);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const numValue = parseInt(tempCustomValue, 10);
            if (!isNaN(numValue) && numValue > 0) {
                onChange('custom', numValue);
                onToggle();
            }
        }
    };

    const displayText = value === 'custom'
        ? `Rows per Category ${customValue}`
        : `Rows per Category ${value}`;

    const predefinedOptions: Array<Exclude<TopItemsOption, 'custom'>> = [10, 25, 50];

    return (
        <div className="tw-relative">
            <style>{noSpinnerStyles}</style>

            <MenuButton
                isOpen={isOpen}
                onToggle={onToggle}
                icon={<CopyWithArrowIcon />}
                label={displayText}
                disabled={disabled}
            />

            {isOpen && (
                <div className="tw-w-48 tw-absolute tw-z-10 tw-mt-1 tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-overflow-hidden tw-min-w-[160px]">
                    <div>
                        {predefinedOptions.map((option) => (
                            <button data-testid="components-topitemsselector-button-1"
                                key={option}
                                className={`tw-flex tw-justify-between tw-items-center tw-w-full tw-p-2 tw-text-left hover:tw-bg-surface-hover tw-border-b-[0.25px] tw-border-b-edge ${value === option ? 'tw-bg-accent-tint' : ''
                                    }`}
                                onClick={() => handleOptionSelect(option)}
                            >
                                <span className={`${value === option ? 'tw-text-accent tw-font-semibold tw-leading-tight' : 'tw-text-ink'}`}>
                                    Rows per Category {option}
                                </span>
                                {value === option && <span className="tw-text-accent"><Check size={16} data-testid="checked-icon" /></span>}
                            </button>
                        ))}

                        <div role="button" data-testid="components-topitemsselector-div-1"
                            className={`tw-flex tw-justify-between tw-items-center tw-w-full tw-p-2 tw-text-left hover:tw-bg-surface-hover tw-border-b-[0.25px] tw-border-b-edge ${value === 'custom' ? 'tw-bg-accent-tint' : ''
                                }`}
                            onClick={() => handleOptionSelect('custom')}
                        >
                            <span className={`${value === 'custom' ? 'tw-text-accent tw-font-semibold tw-leading-tight' : 'tw-text-ink'}`}>
                                Custom
                            </span>
                            {value === 'custom' && <span className="tw-text-accent"><Check size={16} data-testid="checked-icon" /></span>}
                        </div>

                        {value === 'custom' && (
                            <div className="tw-mt-2 tw-px-2 tw-pb-2">
                                <div className="tw-justify-start tw-text-ink-secondary tw-text-xs tw-font-semibold">Rows per category</div>
                                <div className="tw-mt-1 tw-mr-8">
                                    <input data-testid="components-topitemsselector-input-2"
                                        type="number"
                                        className="tw-w-full tw-py-2 tw-px-[5px] tw-outline tw-outline-1 tw-mb-2 tw-mt-1 tw-outline-edge-strong tw-bg-surface tw-text-ink placeholder:tw-text-ink-secondary tw-rounded-md"
                                        value={tempCustomValue}
                                        onChange={handleCustomValueChange}
                                        onKeyDown={handleKeyDown}
                                        min="1"
                                        placeholder="Enter"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

TopItemsSelector.displayName = 'TopItemsSelector';

export default TopItemsSelector;
